import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import {
    Pencil,
    Trash2,
    Pin,
    PinOff,
    MoreHorizontal,
    Bookmark,
    BookmarkX,
    Lock,
    Users,
    Share2,
    History,
    Move,
    UserCog,
    MessageSquarePlus,
    LayoutTemplate,
    Archive,
} from "lucide-react";
import CommentThread from "./CommentThread";
import ConfirmModal from "./ConfirmModal";
import ShareModal from "./ShareModal";
import PageVersionsDrawer from "./PageVersionsDrawer";
import OwnerForm from "./OwnerForm";
import PageMoveForm from "./PageMoveForm";
import SuggestEditForm from "./SuggestEditForm";
import PageToc from "./PageToc";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";
import LimitedAccessBanner from "./LimitedAccessBanner";
import { useBookmarks, reloadBookmarks } from "./useBookmarks";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";

interface PageData {
    id: string;
    categoryId: string;
    folderId: string | null;
    title: string;
    body: string | null;
    status: string;
    hasDraft?: boolean;
    isPinned: boolean;
    isTemplate: boolean;
    ownerId: string | null;
    verificationIntervalDays: number | null;
    lastVerifiedAt: string | null;
    lastVerifiedBy: string | null;
    editingBy: string | null;
    editingSince: string | null;
    currentVersionId: string | null;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string | null;
}

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon?: string | null;
    accessLevel?: string;
}

interface Folder {
    id: string;
    name: string;
    parentFolderId: string | null;
    icon?: string | null;
    color?: string | null;
}

interface Props {
    pageId: string;
    categoryId: string;
    categories: Category[];
    isAdmin: boolean;
    canManage: boolean;
    userId: string | null;
    onNavigateCategory: (catId: string) => void;
    onNavigateFolder?: (folderId: string, categoryId: string) => void;
    onDeleted: () => void;
    onEdit: (page: PageData) => void;
    onChanged?: () => void;
}

export default function PageView({
    pageId,
    categoryId,
    categories,
    isAdmin,
    canManage,
    userId,
    onNavigateCategory,
    onNavigateFolder,
    onDeleted,
    onEdit,
    onChanged,
}: Props) {
    const [page, setPage] = useState<PageData | null>(null);
    const [folder, setFolder] = useState<Folder | null>(null);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [ownerOpen, setOwnerOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [users, setUsers] = useState<Map<string, User>>(new Map());
    const [tocCount, setTocCount] = useState(0);
    const bodyRef = useRef<HTMLDivElement>(null);
    const bookmarks = useBookmarks();

    useEffect(() => {
        loadUsers()
            .then(list => setUsers(new Map(list.map(u => [u.id, u]))))
            .catch(() => { });
    }, []);

    const category = categories.find(c => c.id === categoryId);
    const isRestricted = category?.accessLevel === "restricted";

    useEffect(() => {
        setLoading(true);
        setMenuOpen(false);
        axios.get(`/api/manuals/pages/${pageId}`)
            .then(res => {
                if (res.data.success) {
                    const p = res.data.item;
                    setPage(p);
                    if (p.folderId) {
                        return axios.get(`/api/manuals/folders/${p.folderId}`)
                            .then(fRes => {
                                if (fRes.data.success) setFolder(fRes.data.item);
                            });
                    }
                    setFolder(null);
                }
            })
            .catch(() => toast.error("Failed to load page"))
            .finally(() => setLoading(false));
    }, [pageId]);

    useEffect(() => {
        if (!page) return;
        const prev = document.title;
        document.title = `${page.title} — Policies & Manuals`;
        return () => { document.title = prev; };
    }, [page?.title]);

    useEffect(() => {
        if (!menuOpen) return;
        const handler = () => setMenuOpen(false);
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [menuOpen]);

    const handlePin = () => {
        if (!page) return;
        axios.patch(`/api/manuals/pages/${page.id}/pinned`, { value: !page.isPinned })
            .then(res => {
                if (res.data.success) {
                    setPage(res.data.item);
                    toast.success(page.isPinned ? "Unpinned" : "Pinned");
                    onChanged?.();
                } else {
                    toast.error(res.data.message || "Failed to update");
                }
            })
            .catch(() => toast.error("Failed to update"));
    };

    const handleSaveAsTemplate = () => {
        if (!page) return;
        axios.post(`/api/manuals/pages/${page.id}/save-as-template`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Saved as template");
                    onChanged?.();
                } else {
                    toast.error(res.data.message || "Failed to save");
                }
            })
            .catch(() => toast.error("Failed to save"));
    };

    const handleDelete = () => {
        if (!page) return;
        axios.delete(`/api/manuals/pages/${page.id}`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Page deleted");
                    reloadBookmarks();
                    onDeleted();
                } else {
                    toast.error(res.data.message || "Failed to delete");
                }
            })
            .catch(() => toast.error("Failed to delete page"));
    };

    const handleArchive = () => {
        if (!page) return;
        axios.post(`/api/manuals/pages/${page.id}/archive`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Page archived — restore it anytime from the Archive view");
                    onDeleted();
                } else {
                    toast.error(res.data.message || "Failed to archive");
                }
            })
            .catch(() => toast.error("Failed to archive page"));
    };

    if (loading) return <div className="m-loading"><CustomLoading /></div>;
    if (!page) return (
        <div className="m-empty-state">
            <div style={{ fontSize: 16, fontWeight: 500 }}>Sorry, you are not authorized to view this page</div>
            <div className="m-empty-hint">It may have been moved, deleted, or restricted to another role.</div>
        </div>
    );

    const pills: BreadcrumbPill[] = [
        {
            kind: "category",
            label: category?.name || "Category",
            iconName: category?.icon || null,
            iconColor: category?.color || null,
            onClick: () => onNavigateCategory(categoryId),
        },
    ];
    if (folder) {
        pills.push({
            kind: "folder",
            label: folder.name,
            iconName: folder.icon || null,
            iconColor: folder.color || null,
            onClick: onNavigateFolder ? () => onNavigateFolder(folder.id, categoryId) : undefined,
        });
    }
    pills.push({ kind: "page", label: page.title });

    // Admins manage any page; a non-admin Editor only one they own.
    const canEditPage = isAdmin || (canManage && !!userId && page.ownerId === userId);
    const owner = page.ownerId ? users.get(page.ownerId) : null;
    const ownerName = owner ? displayName(owner) : null;
    const creator = page.createdBy ? users.get(page.createdBy) : null;
    const creatorName = creator ? displayName(creator) : null;
    const editor = page.updatedBy ? users.get(page.updatedBy) : null;
    const editorName = editor ? displayName(editor) : null;

    const headerRight = (
        <>
            {page.ownerId && (
                <span className="m-tip" data-tip={ownerName ? `Owned by ${ownerName}` : "Owner"}>
                    <span
                        className="m-comment-avatar sm"
                        style={{ background: avatarColor(page.ownerId), width: 24, height: 24, fontSize: 10 }}
                    >
                        {ownerName ? initials(ownerName) : "?"}
                    </span>
                </span>
            )}
            <span
                className="m-cat-strip-access m-tip m-tip-right"
                data-tip={isRestricted ? "Visible to invited members only" : "Visible to everyone at National HealthClaim"}
            >
                {isRestricted ? <Lock size={13} /> : <Users size={13} />}
                <span className="m-hide-sm">{isRestricted ? "Invite only" : "Team visible"}</span>
            </span>
            {canEditPage && (
                <button className="m-topbar-btn" onClick={() => onEdit(page)}>
                    <Pencil size={14} /> <span className="m-hide-sm">Edit</span>
                </button>
            )}
            <button className="m-topbar-btn" onClick={() => setShareOpen(true)}>
                <Share2 size={14} /> <span className="m-hide-sm">Share</span>
            </button>
            <div style={{ position: "relative" }}>
                <button
                    className="m-cat-strip-kebab"
                    title="More actions"
                    onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                >
                    <MoreHorizontal size={16} />
                </button>
                {menuOpen && (
                    <div className="m-cat-kebab-menu">
                        <button onClick={() => { setMenuOpen(false); bookmarks.toggle("page", page.id); }}>
                            {bookmarks.keys.has(`page:${page.id}`) ? (
                                <><BookmarkX size={14} /> Remove bookmark</>
                            ) : (
                                <><Bookmark size={14} /> Bookmark</>
                            )}
                        </button>
                        <button onClick={() => { setMenuOpen(false); handlePin(); }}>
                            {page.isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                            {page.isPinned ? "Unpin" : "Pin"}
                        </button>
                        <button onClick={() => { setMenuOpen(false); setVersionsOpen(true); }}>
                            <History size={14} /> Version history
                        </button>
                        {page.status === "published" && (
                            <button onClick={() => { setMenuOpen(false); setSuggestOpen(true); }}>
                                <MessageSquarePlus size={14} /> Suggest edit
                            </button>
                        )}
                        {canManage && (
                            <button onClick={() => { setMenuOpen(false); setMoveOpen(true); }}>
                                <Move size={14} /> Move
                            </button>
                        )}
                        {isAdmin && (
                            <>
                                <button onClick={() => { setMenuOpen(false); setOwnerOpen(true); }}>
                                    <UserCog size={14} /> Change owner
                                </button>
                                {!page.isTemplate && (
                                    <button onClick={() => { setMenuOpen(false); handleSaveAsTemplate(); }}>
                                        <LayoutTemplate size={14} /> Save as template
                                    </button>
                                )}
                            </>
                        )}
                        {isAdmin && (
                            <button onClick={() => { setMenuOpen(false); handleArchive(); }}>
                                <Archive size={14} /> Archive
                            </button>
                        )}
                        {canEditPage && (
                            <button className="m-cat-kebab-danger" onClick={() => { setMenuOpen(false); setDeleteConfirmOpen(true); }}>
                                <Trash2 size={14} /> Delete
                            </button>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div className="m-page-page">
            <Breadcrumb pills={pills} right={headerRight} />

            <LimitedAccessBanner categoryId={categoryId} accessLevel={category?.accessLevel} draft={page.status === "draft"} />

            <div className="m-page-body-wrap">
                <div className={`m-page-layout${tocCount < 2 ? " no-toc" : ""}`}>
                    {tocCount >= 2 ? (
                        <aside className="m-page-toc-col">
                            <PageToc contentRef={bodyRef} html={page.body} onItemCount={setTocCount} />
                        </aside>
                    ) : (
                        <PageToc contentRef={bodyRef} html={page.body} onItemCount={setTocCount} />
                    )}
                    <div className="m-page-body-col">
                        <h1 className="m-page-view-title">{page.title}</h1>
                        <div className="m-page-meta">
                            {page.status === "draft" && <span className="m-content-row-badge draft">draft</span>}
                            {page.hasDraft && canEditPage && (
                                <span className="m-content-row-badge draft" title="You have unpublished draft changes — open the editor to resume or publish them.">draft changes pending</span>
                            )}
                            {page.isPinned && <span className="m-content-row-badge pinned">pinned</span>}
                            {page.isTemplate && <span className="m-content-row-badge template">template</span>}
                            <span className="m-page-meta-text">
                                {creatorName && <>Created by <strong>{creatorName}</strong> · </>}
                                {editorName && editorName !== creatorName
                                    ? <>Updated by <strong>{editorName}</strong> {formatRelative(page.updatedAt)}</>
                                    : <>Updated {formatRelative(page.updatedAt)}</>}
                            </span>
                        </div>

                        <div ref={bodyRef} className="m-page-body">
                            {page.body ? (
                                <div dangerouslySetInnerHTML={{ __html: page.body }} />
                            ) : (
                                <p className="m-page-body-empty">No content yet</p>
                            )}
                        </div>

                        <CommentThread pageId={page.id} isAdmin={isAdmin} currentUserId={userId} />
                    </div>
                </div>
            </div>

            {deleteConfirmOpen && (
                <ConfirmModal
                    title="Delete page"
                    message={`Delete "${page.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => { setDeleteConfirmOpen(false); handleDelete(); }}
                    onCancel={() => setDeleteConfirmOpen(false)}
                />
            )}

            {shareOpen && (
                <ShareModal
                    kind="page"
                    entityId={page.id}
                    label={page.title}
                    url={window.location.href}
                    isRestricted={isRestricted}
                    onClose={() => setShareOpen(false)}
                />
            )}

            {versionsOpen && (
                <PageVersionsDrawer
                    page={page}
                    isAdmin={isAdmin}
                    onRestored={() => {
                        setVersionsOpen(false);
                        axios.get(`/api/manuals/pages/${page.id}`)
                            .then(res => { if (res.data.success) setPage(res.data.item); })
                            .catch(() => { });
                        onChanged?.();
                    }}
                    onClose={() => setVersionsOpen(false)}
                />
            )}

            {ownerOpen && (
                <OwnerForm
                    pageId={page.id}
                    currentOwnerId={page.ownerId}
                    onSave={(ownerId) => {
                        setOwnerOpen(false);
                        setPage(p => p ? { ...p, ownerId } : p);
                    }}
                    onCancel={() => setOwnerOpen(false)}
                />
            )}

            {suggestOpen && (
                <SuggestEditForm
                    pageId={page.id}
                    pageTitle={page.title}
                    currentBody={page.body}
                    onSave={() => setSuggestOpen(false)}
                    onCancel={() => setSuggestOpen(false)}
                />
            )}

            {moveOpen && (
                <PageMoveForm
                    pageId={page.id}
                    pageTitle={page.title}
                    categoryId={page.categoryId}
                    currentFolderId={page.folderId}
                    onSave={() => {
                        setMoveOpen(false);
                        axios.get(`/api/manuals/pages/${page.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    setPage(res.data.item);
                                    const newFolderId = res.data.item.folderId;
                                    if (newFolderId) {
                                        axios.get(`/api/manuals/folders/${newFolderId}`)
                                            .then(fRes => { if (fRes.data.success) setFolder(fRes.data.item); })
                                            .catch(() => { });
                                    } else {
                                        setFolder(null);
                                    }
                                }
                            })
                            .catch(() => { });
                        onChanged?.();
                    }}
                    onCancel={() => setMoveOpen(false)}
                />
            )}
        </div>
    );
}

function formatRelative(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
}
